import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Settings, Save, RotateCcw, Plus, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const FSIConfigPanel = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/fsi-config`, { withCredentials: true });
      setConfig(res.data);
    } catch (err) {
      toast.error("Failed to load FSI config");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/fsi-config`, config, { withCredentials: true });
      toast.success("FSI configuration saved");
    } catch (err) {
      toast.error("Failed to save FSI config");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchConfig();
    toast.info("Configuration reloaded");
  };

  const updateField = (path, value) => {
    setConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = newConfig;
      for (let i = 0; i < keys.length - 1; i++) {
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  const updateFsiRow = (type, index, field, value) => {
    setConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      newConfig[type].fsi_by_road_width[index][field] = parseFloat(value) || 0;
      return newConfig;
    });
  };

  const addFsiRow = (type) => {
    setConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      newConfig[type].fsi_by_road_width.push({ min_width: 0, max_width: 0, fsi: 0, label: "New" });
      return newConfig;
    });
  };

  const removeFsiRow = (type, index) => {
    setConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      newConfig[type].fsi_by_road_width.splice(index, 1);
      return newConfig;
    });
  };

  if (loading) return <p className="text-sm text-muted-foreground p-4">Loading FSI configuration...</p>;
  if (!config) return <p className="text-sm text-destructive p-4">Failed to load configuration</p>;

  const FsiTable = ({ type, title }) => (
    <Card className="rounded-sm shadow-sm" data-testid={`fsi-config-${type}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-heading tracking-tight">{title} - FSI by Road Width</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="grid grid-cols-5 gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider pb-1 border-b">
            <span>Label</span>
            <span>Min Width (m)</span>
            <span>Max Width (m)</span>
            <span>FSI</span>
            <span></span>
          </div>
          {config[type].fsi_by_road_width.map((row, i) => (
            <div key={`${type}-${row.min_width}-${row.max_width}-${i}`} className="grid grid-cols-5 gap-2 items-center">
              <Input
                value={row.label}
                onChange={e => {
                  const newConfig = JSON.parse(JSON.stringify(config));
                  newConfig[type].fsi_by_road_width[i].label = e.target.value;
                  setConfig(newConfig);
                }}
                className="h-8 text-sm"
                data-testid={`fsi-${type}-label-${i}`}
              />
              <Input
                type="number"
                value={row.min_width}
                onChange={e => updateFsiRow(type, i, "min_width", e.target.value)}
                className="h-8 text-sm font-mono"
                data-testid={`fsi-${type}-min-${i}`}
              />
              <Input
                type="number"
                value={row.max_width}
                onChange={e => updateFsiRow(type, i, "max_width", e.target.value)}
                className="h-8 text-sm font-mono"
                data-testid={`fsi-${type}-max-${i}`}
              />
              <Input
                type="number"
                step="0.05"
                value={row.fsi}
                onChange={e => updateFsiRow(type, i, "fsi", e.target.value)}
                className="h-8 text-sm font-mono font-bold"
                data-testid={`fsi-${type}-fsi-${i}`}
              />
              <Button variant="ghost" size="sm" onClick={() => removeFsiRow(type, i)} className="h-8 w-8 p-0 text-destructive">
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => addFsiRow(type)} className="mt-2" data-testid={`fsi-${type}-add`}>
            <Plus className="h-3 w-3 mr-1" /> Add Row
          </Button>
        </div>

        {type === "non_tod" && (
          <div className="mt-4 pt-3 border-t grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Paid FSI (fixed)</Label>
              <Input type="number" step="0.1" value={config.non_tod.paid_fsi} onChange={e => updateField("non_tod.paid_fsi", parseFloat(e.target.value) || 0)} className="h-8 text-sm font-mono" data-testid="non-tod-paid-fsi" />
            </div>
            <div>
              <Label className="text-xs">TDR</Label>
              <p className="text-xs text-muted-foreground mt-1">Remaining FSI - Paid FSI</p>
            </div>
          </div>
        )}

        {type === "tod" && (
          <div className="mt-4 pt-3 border-t grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">TDR %</Label>
              <Input type="number" value={config.tod.tdr_percent} onChange={e => updateField("tod.tdr_percent", parseFloat(e.target.value) || 0)} className="h-8 text-sm font-mono" data-testid="tod-tdr-pct" />
            </div>
            <div>
              <Label className="text-xs">Paid FSI %</Label>
              <Input type="number" value={config.tod.paid_fsi_percent} onChange={e => updateField("tod.paid_fsi_percent", parseFloat(e.target.value) || 0)} className="h-8 text-sm font-mono" data-testid="tod-paid-fsi-pct" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="fsi-config-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2">
          <Settings className="h-5 w-5" /> FSI Configuration
        </h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleReset} data-testid="fsi-config-reset">
            <RotateCcw className="h-4 w-4 mr-1" /> Reset
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="fsi-config-save">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {/* Construction Costs by Building Height */}
      <Card className="rounded-sm shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading tracking-tight">Construction Costs by Building Height</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">24 metres (₹/sq.ft)</Label>
              <Input 
                type="number" 
                value={config.construction_costs?.["24"] || 2800} 
                onChange={e => {
                  const newConfig = JSON.parse(JSON.stringify(config));
                  if (!newConfig.construction_costs) newConfig.construction_costs = {};
                  newConfig.construction_costs["24"] = parseInt(e.target.value) || 0;
                  setConfig(newConfig);
                }} 
                className="h-8 text-sm font-mono" 
                data-testid="construction-cost-24" 
              />
            </div>
            <div>
              <Label className="text-xs">45 metres (₹/sq.ft)</Label>
              <Input 
                type="number" 
                value={config.construction_costs?.["45"] || 3200} 
                onChange={e => {
                  const newConfig = JSON.parse(JSON.stringify(config));
                  if (!newConfig.construction_costs) newConfig.construction_costs = {};
                  newConfig.construction_costs["45"] = parseInt(e.target.value) || 0;
                  setConfig(newConfig);
                }} 
                className="h-8 text-sm font-mono" 
                data-testid="construction-cost-45" 
              />
            </div>
            <div>
              <Label className="text-xs">70 metres (₹/sq.ft)</Label>
              <Input 
                type="number" 
                value={config.construction_costs?.["70"] || 3600} 
                onChange={e => {
                  const newConfig = JSON.parse(JSON.stringify(config));
                  if (!newConfig.construction_costs) newConfig.construction_costs = {};
                  newConfig.construction_costs["70"] = parseInt(e.target.value) || 0;
                  setConfig(newConfig);
                }} 
                className="h-8 text-sm font-mono" 
                data-testid="construction-cost-70" 
              />
            </div>
            <div>
              <Label className="text-xs">100 metres + (₹/sq.ft)</Label>
              <Input 
                type="number" 
                value={config.construction_costs?.["100"] || 4200} 
                onChange={e => {
                  const newConfig = JSON.parse(JSON.stringify(config));
                  if (!newConfig.construction_costs) newConfig.construction_costs = {};
                  newConfig.construction_costs["100"] = parseInt(e.target.value) || 0;
                  setConfig(newConfig);
                }} 
                className="h-8 text-sm font-mono" 
                data-testid="construction-cost-100" 
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">These costs are auto-applied when user selects building height in the calculator.</p>
        </CardContent>
      </Card>

      {/* Basic FSI */}
      <Card className="rounded-sm shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading tracking-tight">General Parameters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">Basic FSI</Label>
              <Input type="number" step="0.1" value={config.basic_fsi} onChange={e => updateField("basic_fsi", parseFloat(e.target.value) || 0)} className="h-8 text-sm font-mono" data-testid="basic-fsi" />
            </div>
            <div>
              <Label className="text-xs">MHADA Permissible FSI</Label>
              <Input type="number" step="0.1" value={config.mhada.permissible_fsi} onChange={e => updateField("mhada.permissible_fsi", parseFloat(e.target.value) || 0)} className="h-8 text-sm font-mono" data-testid="mhada-fsi" />
            </div>
            <div>
              <Label className="text-xs">MHADA Incentive FSI (sq m per member)</Label>
              <Input type="number" step="0.1" value={config.mhada.incentive_fsi_per_member || 26.20} onChange={e => updateField("mhada.incentive_fsi_per_member", parseFloat(e.target.value) || 0)} className="h-8 text-sm font-mono" data-testid="mhada-incentive-fsi" />
            </div>
            <div>
              <Label className="text-xs">Incentive FSI</Label>
              <Input type="number" step="0.1" value={config.incentive_fsi.value} onChange={e => updateField("incentive_fsi.value", parseFloat(e.target.value) || 0)} className="h-8 text-sm font-mono" data-testid="incentive-fsi" />
            </div>
            <div>
              <Label className="text-xs">Min Building Age (years)</Label>
              <Input type="number" value={config.incentive_fsi.min_building_age} onChange={e => updateField("incentive_fsi.min_building_age", parseInt(e.target.value) || 0)} className="h-8 text-sm font-mono" data-testid="min-building-age" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* FSI Tables */}
      <FsiTable type="non_tod" title="Non-TOD" />
      <FsiTable type="tod" title="TOD" />

      {/* Ancillary & Green FSI */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="rounded-sm shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading tracking-tight">Ancillary FSI</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Residential %</Label>
              <Input type="number" value={config.ancillary.residential_percent} onChange={e => updateField("ancillary.residential_percent", parseFloat(e.target.value) || 0)} className="h-8 text-sm font-mono" data-testid="ancillary-res" />
            </div>
            <div>
              <Label className="text-xs">Commercial %</Label>
              <Input type="number" value={config.ancillary.commercial_percent} onChange={e => updateField("ancillary.commercial_percent", parseFloat(e.target.value) || 0)} className="h-8 text-sm font-mono" data-testid="ancillary-com" />
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-sm shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading tracking-tight">Green FSI</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Silver %</Label>
              <Input type="number" value={config.green_fsi.silver_percent} onChange={e => updateField("green_fsi.silver_percent", parseFloat(e.target.value) || 0)} className="h-8 text-sm font-mono" data-testid="green-silver" />
            </div>
            <div>
              <Label className="text-xs">Gold %</Label>
              <Input type="number" value={config.green_fsi.gold_percent} onChange={e => updateField("green_fsi.gold_percent", parseFloat(e.target.value) || 0)} className="h-8 text-sm font-mono" data-testid="green-gold" />
            </div>
            <div>
              <Label className="text-xs">Platinum %</Label>
              <Input type="number" value={config.green_fsi.platinum_percent} onChange={e => updateField("green_fsi.platinum_percent", parseFloat(e.target.value) || 0)} className="h-8 text-sm font-mono" data-testid="green-platinum" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 6 Meter Road */}
      <Card className="rounded-sm shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading tracking-tight">6 Meter Road Rules</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Min Width (m)</Label>
            <Input type="number" value={config.six_meter_road.min_width} onChange={e => updateField("six_meter_road.min_width", parseFloat(e.target.value) || 0)} className="h-8 text-sm font-mono" data-testid="6m-min" />
          </div>
          <div>
            <Label className="text-xs">Max Width (m)</Label>
            <Input type="number" value={config.six_meter_road.max_width} onChange={e => updateField("six_meter_road.max_width", parseFloat(e.target.value) || 0)} className="h-8 text-sm font-mono" data-testid="6m-max" />
          </div>
          <div className="flex items-end">
            <p className="text-xs text-muted-foreground">Basic FSI only, No TDR, No Paid FSI</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FSIConfigPanel;
